import { useAuth } from '@/lib/auth/AuthContext';
import { usePatientConfiguration } from '@/features/patient-configuration/api';
import { CustomFieldDialog } from '@/features/patient-configuration/CustomFields';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { PatientsAssistant } from '@/features/assistant/PatientsAssistant';
import { CareRoster, defaultRosterView, type RosterView } from '@/features/flowsheets/CareRoster';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useOrgDateFormat } from '@/lib/timezone';
import { SearchInput } from '../components/SearchInput';
import { RecordCount } from '../components/RecordCount';
import { LoadingState } from '../components/LoadingState';
import { PatientAvatar } from '../components/PatientAvatar';
import { useIsPersonalEdition } from '@/lib/auth/useEdition';
import { P, RequirePermission } from '@/features/permissions';
import { SimpleTable } from '@/components/table/simple-table';
import { PersonalHomeTiles } from '../components/PersonalHomeTiles';
import { usePageHeaderSetter } from '@/components/layout/PageHeaderContext';
import { type PatientOut, usePatients } from '../api/patients-api';

export function PatientsPage() {
  const { organization, user } = useAuth();
  const preferencesKey = `patient-columns:${organization?.organization_id}:${user?.id}`;
  const navigate = useNavigate();
  const setPageHeader = usePageHeaderSetter();
  const isPersonal = useIsPersonalEdition();
  const [search, setSearch] = useState('');
  const config = usePatientConfiguration();
  const [fieldDialog, setFieldDialog] = useState(false);
  const routeSearch = useSearch({ strict: false }) as { roster?: RosterView };
  const [view, setView] = useState<RosterView>(defaultRosterView);
  const [command, setCommand] = useState<{ id: string; view: RosterView }>();
  useEffect(() => {
    if (routeSearch.roster) setCommand({ id: crypto.randomUUID(), view: routeSearch.roster });
  }, [routeSearch.roster]);
  const { data, isLoading } = usePatients({
    limit: 100,
    allPages: true,
    search: !isPersonal && search ? search : undefined,
  });

  const total = usePatients({ limit: 1 });

  useEffect(() => {
    setPageHeader({
      title: isPersonal ? 'Home' : 'Patients',
      actions: (
        <div className="flex items-center gap-2">
          {!isPersonal && <PatientsAssistant view={view} onRoster={setCommand} />}
          {!isPersonal && <SearchInput value={search} placeholder="Search by name or MRN..." onChange={setSearch} />}
          <RequirePermission permission={P.CREATE_PATIENT}>
            <Button size="sm" onClick={() => navigate({ to: '/patients/new' })}>
              <Plus className="mr-2 h-4 w-4" />
              {isPersonal ? 'Add User' : 'Add Patient'}
            </Button>
          </RequirePermission>
        </div>
      ),
    });
    return () => setPageHeader(null);
  }, [setPageHeader, navigate, isPersonal, view, search]);

  const columns = useMemo(
    () => [
      ...buildColumns(isPersonal),
      ...(config.data?.fields ?? []).map((f) => ({
        id: f.id,
        header: f.label,
        meta: { label: f.label },
        accessorFn: (row: PatientOut) => row.metadata_?.custom_fields?.[f.id] ?? '',
      })),
    ],
    [isPersonal, config.data],
  );

  const allPatients = useMemo(() => data?.data ?? [], [data]);

  const patients = allPatients;

  if (isLoading) return <LoadingState />;

  return (
    <div className={`flex flex-col gap-6 p-4 md:p-6 ${isPersonal ? 'mx-auto w-full max-w-3xl' : ''}`}>
      {/* Personal-edition action tiles — Quick Start + camera scan */}
      {isPersonal && <PersonalHomeTiles />}

      {!isPersonal && <CareRoster command={command} onViewChange={setView} />}

      {/* Table */}
      <SimpleTable<PatientOut>
        key={preferencesKey}
        preferencesKey={preferencesKey}
        tableCaption={
          <p className="px-2 text-xs text-muted-foreground">
            {total.data?.meta.total_items ?? data?.meta.total_items ?? 0} patients total
          </p>
        }
        columnActions={
          <RequirePermission permission="manage_organization">
            <DropdownMenuItem onSelect={() => setFieldDialog(true)}>Create custom column…</DropdownMenuItem>
          </RequirePermission>
        }
        data={patients}
        columns={columns}
        getRowId={(row) => row.id}
        onRowClick={(row) => navigate({ to: '/patients/$patientId', params: { patientId: row.id } })}
      />

      <CustomFieldDialog open={fieldDialog} onOpenChange={setFieldDialog} />
      <RecordCount entity="patients" filtered={patients.length} total={allPatients.length} />
    </div>
  );
}

function buildColumns(isPersonal: boolean): ColumnDef<PatientOut>[] {
  const mrnColumn: ColumnDef<PatientOut> = {
    id: 'mrn',
    size: 160,
    header: 'MRN',
    meta: { label: 'MRN' },
    accessorKey: 'medical_record_number',
    cell: ({ row }) => <span className="font-mono text-sm">{row.original.medical_record_number}</span>,
  };
  const nameLabel = isPersonal ? 'Username' : 'Patient Name';
  const nameColumn: ColumnDef<PatientOut> = {
    id: 'name',
    header: nameLabel,
    meta: { label: nameLabel },
    accessorFn: (row) => {
      const { last, first } = getPatientName(row.metadata_);
      return `${last} ${first}`.trim();
    },
    cell: ({ row }) => {
      const { last, first } = getPatientName(row.original.metadata_);
      const displayName = first || last ? `${last}, ${first}` : row.original.medical_record_number;
      return (
        <div className="flex items-center gap-3">
          <PatientAvatar lastName={last} firstName={first} />
          <p className="font-medium">{displayName}</p>
          {row.original.metadata_?.intake_status === 'draft' && <Badge variant="outline">Draft</Badge>}
        </div>
      );
    },
  };
  const sourceColumn: ColumnDef<PatientOut> = {
    id: 'source',
    header: 'Source',
    meta: { label: 'Source' },
    accessorKey: 'patient_source',
    cell: ({ row }) =>
      row.original.patient_source ? (
        <Badge variant="outline" className="text-xs">
          {row.original.patient_source}
        </Badge>
      ) : null,
  };
  const createdColumn: ColumnDef<PatientOut> = {
    id: 'created',
    header: 'Created',
    accessorKey: 'created_at',
    meta: { label: 'Created' },
    cell: ({ row }) => <CreatedCell value={row.original.created_at} />,
  };
  const cols: ColumnDef<PatientOut>[] = [nameColumn, sourceColumn, createdColumn];
  if (!isPersonal) {
    cols.splice(0, 0, mrnColumn);
  }
  return cols;
}

// The `created` column's cell renderer is a plain function inside a
// TanStack column definition, not a component — `useOrgDateFormat` (a hook)
// can't be called there. Extracted so the hook has a component to live in.
function CreatedCell({ value }: { value: string }) {
  const { formatInstant } = useOrgDateFormat();
  return <span className="text-sm text-muted-foreground">{formatInstant(value, 'MMM d, yyyy')}</span>;
}

function getPatientName(metadata: import('../api/patients-api').PatientMetadata | null): {
  first: string;
  last: string;
} {
  if (!metadata) return { last: '', first: '' };
  return {
    last: metadata.last_name ?? '',
    first: metadata.first_name ?? '',
  };
}
